import { Config, isLoopbackUrl } from "@amtana/module";
import { z } from "zod";

// The secrets behind the seams every remote-agent lane builds to (ADR-023, epic 27): the turn
// token's HMAC key (seam 1), the key each computer's agent tokens are derived under (seam 2;
// a guest holds only its own computer's derived key, S27.14), the pool host's bearer for the internal
// doors it calls (seam 3), and our model key (§7). Development and tests run on fixed
// placeholders; production refuses each one while it is still the placeholder. The model key has
// no default: unset, our model is not offered (D550). `pnpm dev` sets it to the model simulator's
// own development key, which production refuses, and the model's base defaults to the simulator,
// which production refuses too (D678).

export const DEV_AGENT_TURN_SECRET = "dev-agent-turn-secret-".padEnd(40, "t");
/** vmdsim's own (`VMDSIM_AGENT_TOKEN_SECRET`), so the api's hello meets its supervisors in
 * development with no setting (D580). */
export const DEV_AGENT_TOKEN_SECRET = "vmdsim-agent-token-secret-for-development-only";
/** The egress proxy's own development default (services/egress), so the two meet in development. */
export const DEV_POOL_HOST_TOKEN = "p".repeat(32);

/** modelsim (sims/modelsim), standing in for the model's API in development and tests (D678). */
export const DEV_AGENT_MODEL_BASE_URL = "http://127.0.0.1:4331";
/** modelsim's own key (`MODELSIM_KEY`), which `pnpm dev` hands the api as `AGENT_MODEL_KEY`. */
export const DEV_AGENT_MODEL_KEY = "modelsim-key-for-development-only";

/** A model's name as the Messages API takes it. */
const modelName = z.string().regex(/^claude-[a-z0-9.-]{1,60}$/, "a model name such as claude-sonnet-4-5");

const secret = z
  .string()
  .min(32, "needs 32 characters or more: an HMAC key or a bearer is only as strong as its secret.");

const refuse = (placeholder: string) => (value: string | undefined) =>
  value === placeholder
    ? "still the development placeholder; anyone who read the repository could forge with it"
    : undefined;

export const agentConfig = Config.define((c) => ({
  turnSecret: c.env("AGENT_TURN_SECRET", secret.default(DEV_AGENT_TURN_SECRET), {
    refuseInProduction: refuse(DEV_AGENT_TURN_SECRET),
  }),
  tokenSecret: c.env("AGENT_TOKEN_SECRET", secret.default(DEV_AGENT_TOKEN_SECRET), {
    refuseInProduction: refuse(DEV_AGENT_TOKEN_SECRET),
  }),
  poolHostToken: c.env("POOL_HOST_TOKEN", secret.default(DEV_POOL_HOST_TOKEN), {
    refuseInProduction: refuse(DEV_POOL_HOST_TOKEN),
  }),
  /** Our model's API key; unset, `zaistev-model` is neither offered nor accepted (D550). It never
   * leaves the api: the model channel adds it to each call the model door passes on (D670). */
  modelKey: c.env("AGENT_MODEL_KEY", z.string().min(1).optional(), {
    refuseInProduction: (value) =>
      value === DEV_AGENT_MODEL_KEY ? "still the model simulator's development key" : undefined,
  }),
  /** Where our model's Messages API lives: `https://api.anthropic.com` in production (D678). */
  modelBaseUrl: c.env("AGENT_MODEL_BASE_URL", z.url().default(DEV_AGENT_MODEL_BASE_URL), {
    refuseInProduction: (value) =>
      isLoopbackUrl(value) ? "points at a loopback host, the model simulator" : undefined,
  }),
  /** The model a turn runs on, whatever the harness asks for (D674). */
  modelName: c.env("AGENT_MODEL_NAME", modelName.default("claude-sonnet-4-5")),
  /** The small model a harness asks for its quick side calls (a name with "haiku" in it). */
  modelSmallName: c.env("AGENT_MODEL_SMALL_NAME", modelName.default("claude-haiku-4-5")),
}));
